"use client";

import { ArrowRight, Flower2, Trees } from "lucide-react";
import type { GardenType } from "./GardenTypeToggle";

interface Props {
  onChoose: (t: GardenType) => void;
}

const OPTIONS: {
  value: GardenType;
  title: string;
  body: string;
  Icon: typeof Trees;
}[] = [
  {
    value: "outdoor",
    title: "Outdoor Garden",
    body: "Upload a photo of your yard and we'll design a planting for it.",
    Icon: Trees,
  },
  {
    value: "container",
    title: "Container Garden",
    body: "Pick a planter and we'll fill it with a thriller, fillers and spillers.",
    Icon: Flower2,
  },
];

/**
 * Step 1 of the Designer for a garden center with the CONTAINER_GARDEN add-on
 * (PLA2-966): "What are we planting?". Outdoor continues to the photo upload
 * on the same page; Container opens /design/container. Uses the planter
 * cards' visual language (white card, green border on hover).
 */
export function GardenTypeChooser({ onChoose }: Props) {
  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="mb-2 text-center font-display text-3xl font-bold text-[#0f2a1d] dark:text-plantista-50 md:text-4xl">
        What are we planting?
      </h1>
      <p className="mb-8 text-center text-[#5d6b63] dark:text-plantista-300">
        Choose the kind of garden to design.
      </p>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        {OPTIONS.map(({ value, title, body, Icon }) => (
          <button
            key={value}
            type="button"
            onClick={() => onChoose(value)}
            className="group flex flex-col items-start gap-4 rounded-2xl border-2 border-[#e6ebe7] bg-white p-6 text-left transition-all hover:border-[#2ecb8d] hover:shadow-md focus-visible:border-[#2ecb8d] focus-visible:outline-none dark:border-plantista-800 dark:bg-plantista-900 dark:hover:border-plantista-400 sm:p-8"
          >
            <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-plantista-50 text-[#14a46c] dark:bg-plantista-800 dark:text-plantista-300">
              <Icon className="h-8 w-8" aria-hidden />
            </span>
            <span>
              <span className="block font-display text-xl font-bold text-[#0f2a1d] dark:text-plantista-50">
                {title}
              </span>
              <span className="mt-1 block text-sm text-[#5d6b63] dark:text-plantista-300">
                {body}
              </span>
            </span>
            <span className="mt-auto inline-flex items-center gap-1 text-sm font-semibold text-[#14a46c] dark:text-plantista-300">
              Start{" "}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
